import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Chip,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Folder,
} from '@mui/icons-material';
import { categoryAPI } from '../../services/api';
import toast from 'react-hot-toast';

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'folder',
    color: '#1976d2',
    order: 0,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await categoryAPI.getAll({ includeInactive: true });
      setCategories(response.data.categories || []);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors du chargement des catégories';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Category list error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategory(null);
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      description: '',
      icon: 'folder',
      color: '#1976d2',
      order: categories.length,
    });
    setEditDialogOpen(true);
  };

  const handleOpenEditDialog = () => {
    if (!selectedCategory) return;
    setFormData({
      name: selectedCategory.name,
      description: selectedCategory.description || '',
      icon: selectedCategory.icon || 'folder',
      color: selectedCategory.color || '#1976d2',
      order: selectedCategory.order || 0,
    });
    setAnchorEl(null);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setFormData({
      name: '',
      description: '',
      icon: 'folder',
      color: '#1976d2',
      order: 0,
    });
    setSelectedCategory(null);
  };

  const handleSaveCategory = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom de la catégorie est requis');
      return;
    }

    try {
      if (selectedCategory) {
        await categoryAPI.update(selectedCategory.id, formData);
        toast.success('Catégorie mise à jour avec succès');
      } else {
        await categoryAPI.create(formData);
        toast.success('Catégorie créée avec succès');
      }
      handleCloseEditDialog();
      await fetchCategories();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de la sauvegarde';
      toast.error(errorMsg);
      console.error('Save category error:', err);
    }
  };

  const handleOpenDeleteDialog = () => {
    setAnchorEl(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategory) return;
    
    setDeleting(true);
    try {
      await categoryAPI.delete(selectedCategory.id);
      toast.success('Catégorie supprimée avec succès');
      handleCloseDeleteDialog();
      await fetchCategories();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const colors = [
    '#e91e63', '#9c27b0', '#2196f3', '#00bcd4',
    '#4caf50', '#ff9800', '#f44336', '#795548',
    '#607d8b', '#1976d2'
  ];

  const icons = [
    'folder', 'healing', 'person', 'mic', 'psychology',
    'favorite', 'child_care', 'self_improvement', 'account_circle'
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Gestion des Catégories
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
        >
          Créer une Catégorie
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {categories.map((category) => (
          <Grid item xs={12} md={6} lg={4} key={category.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: category.color || '#1976d2',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Folder sx={{ fontSize: 16, color: 'white' }} />
                      </Box>
                      <Typography variant="h6">
                        {category.name}
                      </Typography>
                    </Box>
                    {category.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {category.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={category.quizCount || 0}
                        size="small"
                        variant="outlined"
                      />
                      {!category.isActive && (
                        <Chip
                          label="Inactive"
                          size="small"
                          color="default"
                        />
                      )}
                    </Box>
                  </Box>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, category)}
                    size="small"
                  >
                    <MoreVert />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {categories.length === 0 && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucune catégorie créée
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Commencez par créer votre première catégorie
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenCreateDialog}
            >
              Créer une Catégorie
            </Button>
          </CardContent>
        </Card>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleOpenEditDialog}>
          <Edit sx={{ mr: 1 }} />
          Modifier
        </MenuItem>
        <MenuItem onClick={handleOpenDeleteDialog} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} />
          Supprimer
        </MenuItem>
      </Menu>

      {/* Dialog de création/édition */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedCategory ? 'Modifier la catégorie' : 'Créer une catégorie'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Nom de la catégorie"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Icône</InputLabel>
              <Select
                value={formData.icon}
                label="Icône"
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                {icons.map((icon) => (
                  <MenuItem key={icon} value={icon}>
                    {icon}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Couleur</InputLabel>
              <Select
                value={formData.color}
                label="Couleur"
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              >
                {colors.map((color) => (
                  <MenuItem key={color} value={color}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          bgcolor: color
                        }}
                      />
                      {color}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="number"
              label="Ordre d'affichage"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleSaveCategory} variant="contained" color="primary">
            {selectedCategory ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer la catégorie "<strong>{selectedCategory?.name}</strong>" ?
            <br /><br />
            Cette action est irréversible. Les quiz associés devront être déplacés ou supprimés.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog} 
            disabled={deleting}
            color="inherit"
          >
            Annuler
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            disabled={deleting}
            color="error" 
            variant="contained"
            autoFocus
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Suppression...' : 'Supprimer définitivement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryList;

